/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { getImageUrl } from "@Utils";
import { includedProjectsUrl } from "@Services/dataManager";
import { useTranslation } from "@i18n";
import React, { useEffect, useRef, useState } from "react";
import DropdownButtonComponent from "@CubeComponents/form/DropdownButtonComponentV1";
import cross from "../../assets/icons/svg/crossWhite.svg";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import SlickGrid1 from "../../cubeComponent/html/SlickGrid1";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { getSelectProjectsGridColumns } from "@Features/reviews/helpers";
import type { ReviewSelectProjectRow } from "@Features/reviews/components/Reviews/ReviewsHelper";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { useSelector } from "react-redux";
import { getUrlForImage } from "../../cubeComponent/constants/globalFunctions";
import { Chip } from "@mui/material";
import { RootState } from "@Reducers/index";
import type { SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";

type ReviewToolbarProps = {
  /** The active due-date filter key ("overdue" / "next7days" / "next30days"), or null for none. */
  activeFilter: string | null;
  onFilterChange: (filter: string | null) => void;
  selectedProjectIds?: ObjectIdString[];
  onProjectIdChange: (projectIds: ObjectIdString[]) => void;
};

function ReviewToolbar({ activeFilter, onFilterChange, selectedProjectIds, onProjectIdChange }: ReviewToolbarProps) {
  const { t } = useTranslation();
  const [selectedProjectItemId, setSelectedProjectItemId] = useState("My Projects");
  const [isAddProjectsModalVisible, setIsAddProjectsModalVisible] = useState({
    show: false,
  });
  const layout = useSelector((state: RootState) => state.layout);
  const [hasSelection, setHasSelection] = useState(false);
  const selectedProjectsRef = useRef<SlickGrid1Ref<ReviewSelectProjectRow> | null>(null);
  const [projectsQuery] = useState(() => ({
    originSource: "PROJECT",
    where: [
      {
        field: "archivedStatus",
        operator: "equal",
        value: 0,
        ignoreCase: false,
      },
    ],
  }));

  useEffect(() => {
    onProjectIdChange([]);
  }, [selectedProjectItemId]);

  const handleProjectSelection = (selectedItem: string | number) => {
    // The dropdown hands back `string | number`; the two items it renders are keyed by name.
    setSelectedProjectItemId(String(selectedItem));
    if (selectedItem === "Specific Projects") {
      setIsAddProjectsModalVisible({ show: true });
    } else if (selectedItem === "My Projects") {
      onProjectIdChange([]);
    }
  };

  const closeAddProjectsModal = () => {
    if (selectedProjectsRef.current) {
      const selectedProjects = selectedProjectsRef.current.dataView.getAllSelectedItems();
      if (selectedProjects.length == 0) {
        setSelectedProjectItemId("My Projects");
      }
    }
    selectedProjectsRef.current = null;
    setIsAddProjectsModalVisible({ show: false });
  };

  const onSubmitActionOfSelectedProjects = () => {
    if (selectedProjectsRef.current) {
      const selectedProjects = selectedProjectsRef.current.dataView.getAllSelectedItems();
      // Grid rows carry the untyped `RowId`, so the ids are parsed rather than asserted: a row
      // without a real ObjectId is dropped instead of reaching `onProjectIdChange` branded.
      const idsArray = selectedProjects
        .map((project) => objectIdString.safeParse(project._id).data)
        .filter((id): id is ObjectIdString => id !== undefined);
      onProjectIdChange(idsArray);
    }
    handleProjectSelection("Specific Projects");
    closeAddProjectsModal();
  };

  const renderFilterChip = (id: string, label: string, key: string) => {
    const isActive = activeFilter === key;

    return (
      <Chip
        key={id}
        label={
          <div className="flex items-center gap-1 font-semibold text-sm">
            {label}
            {isActive && <img src={cross} alt="clear" />}
          </div>
        }
        onClick={() => onFilterChange(isActive ? null : key)}
        sx={{
          backgroundColor: isActive ? "#666A84" : "#ECF3FE",
          color: isActive ? "#FFFFFF" : "var(--Primary-Dark, #192048)",
          borderRadius: "62.4375rem",
          "&:hover, &:active": {
            backgroundColor: isActive ? "#666A84" : "#ECF3FE",
          },
          ".MuiChip-label": {
            padding: "1rem",
          },
        }}
      />
    );
  };

  const actionTemplateProjects = () => (
    <div className="flex items-center gap-3">
      <span>{t("Project")}</span>
      <DropdownButtonComponent
        content={selectedProjectItemId === "Specific Projects" ? "Specific Projects" : "My Projects"}
        select={handleProjectSelection}
        items={[
          { text: "Specific Projects", id: "Specific Projects" },
          { text: "My Projects", id: "My Projects" },
        ]}
      />
    </div>
  );

  const addProjectsHeader = () => (
    <div className="flex justify-between w-full">
      <span>{t("Select Project")}</span>
    </div>
  );

  const projectImgDescTemplate = (args: ReviewSelectProjectRow) => {
    const container = document.createElement("div");
    const root = createRoot(container);

    let imageUrl = getImageUrl() + (layout.PROJECT_LOGO ?? "");
    if (args.image !== "") {
      // `id` is the grid's `RowId` (string | number | stringable); `getUrlForImage` takes a string.
      imageUrl = getUrlForImage({ id: args.id?.toString() }, "projectImage");
    }

    root.render(
      <div className="flex items-center justify-center">
        <img src={imageUrl} alt="Logo" width="50" height="40" className="object-contain" />
        <span className="ps-2">{args.projectId}</span>
      </div>,
    );

    return container;
  };

  const [selectProjectsGridColumns] = useState(
    getSelectProjectsGridColumns({
      projectImgDescTemplate,
    }),
  );

  const addProjectsContent = () => (
    <div className="h-80 overflow-hidden">
      <SlickGrid1
        ref={selectedProjectsRef}
        disableVirtualisation
        onReactGridCreated={() => {
          if (selectedProjectsRef.current && selectedProjectIds?.length) {
            selectedProjectsRef.current.dataView.setSelectedIds(selectedProjectIds);
          }
        }}
        remoteDataSource={includedProjectsUrl}
        query={projectsQuery}
        enableFilteringOption
        enableSortingOption
        columnDefinitions={selectProjectsGridColumns}
        gridOptions={{ forceFitColumns: true }}
        onSelectedRowIdsChanged={() => {
          if (selectedProjectsRef.current) {
            const selected = selectedProjectsRef.current.dataView.getAllSelectedItems();
            setHasSelection(selected.length > 0);
          }
        }}
      />
    </div>
  );

  const addProjectsFooter = () => (
    <div className="flex justify-end p-4 gap-4">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        disabled={!hasSelection}
        onClick={onSubmitActionOfSelectedProjects}
      >
        {t("Add")}
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeAddProjectsModal}>
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );

  return (
    <>
      <div className="flex flex-wrap items-center gap-4 py-2 px-4 bg-white shadow-sm rounded-md ps-8">
        {
          // Need to Include after super admin pemission development
          /* <div className="flex items-center gap-3">
          <span>{t("View As")}</span>
          <DropdownButtonComponent
            content="Logged In User"
            select={onUserChange}
            disabled
            items={[
              { text: "Logged in User", id: 1 },
              { text: "Other Users", id: 2 },
            ]}
          />
        </div> */
        }

        {actionTemplateProjects()}

        {renderFilterChip("filter_overdue", "Overdue", "overdue")}
        {renderFilterChip("filter_next_7_days", "Next 7 Days", "next7days")}
        {renderFilterChip("filter_next_30_days", "Next 30 Days", "next30days")}
      </div>

      {isAddProjectsModalVisible.show && (
        <ModalStateDialogComponent
          isVisible
          headerTitle={addProjectsHeader()}
          modelContent={addProjectsContent()}
          footerContent={addProjectsFooter()}
          onCancel={closeAddProjectsModal}
          allowModalPadding={false}
          width="62.5rem"
        />
      )}
    </>
  );
}

export default ReviewToolbar;
